import { TexturedMaterial } from './TexturedMaterial';
import { InterpolatedProperty } from '../properties/InterpolatedProperty';
import { SimpleSettingsProperty } from "../properties/SimpleSettingsProperty";
import * as THREE from 'three';
import { BlendMode, Side } from './Material';
import { Color } from '../types';
import { Logger } from '../core/Logger';
import { POIData } from '../poi/POIData';



export class VideoMaterial extends TexturedMaterial {
    // Video-specific properties
    public readonly autoplay: SimpleSettingsProperty<boolean>;
    public readonly loop: SimpleSettingsProperty<boolean>;
    public readonly muted: SimpleSettingsProperty<boolean>;
    public readonly volume: SimpleSettingsProperty<number>;
    public readonly playbackRate: SimpleSettingsProperty<number>;
    public readonly currentTime: SimpleSettingsProperty<number>;
    public readonly blendMode: SimpleSettingsProperty<BlendMode>;
    
    // Video element and loading state
    private _video: HTMLVideoElement | null = null;
    private _isPlaying: boolean = false;
    private _lastLogTime: number = 0;
    private _manuallyPaused: boolean = false; // Track if user manually paused
    public readonly playOnActive: SimpleSettingsProperty<boolean>;

    // HLS.js instance (created only when needed)
    private _hls: any | null = null;
    // If lazy-init deferred the load, this holds the pending URL until setActive() fires it.
    private _deferredLoadUrl: string | null = null;
    // Set when setActive() fires before loadTexture finished — the canplay event will trigger play.
    private _pendingPlay: boolean = false;

    // Shared cache for video instances (one decoder/texture per URL)
    private static _cache: Map<string, { video: HTMLVideoElement; texture: THREE.VideoTexture; hls?: any; ref: number; aspect: number }>
        = new Map();

    constructor(
        name: string = 'VideoMaterial',
        poiData: POIData
    ) {
        super('video', name, poiData);
        
        // Test logger connection immediately
        //Logger.Instance.Info(`🎬 VideoMaterial '${name}' created`);
        
        const groupName = "Video Control";
        this.autoplay = new SimpleSettingsProperty('Autoplay', null, false, groupName);
        this.loop = new SimpleSettingsProperty('Loop', null, true, groupName);
        this.muted = new SimpleSettingsProperty('Muted', null, true, groupName);
        this.volume = new SimpleSettingsProperty("Volume", null, 1.0, groupName);
        this.playbackRate = new SimpleSettingsProperty("Playback Rate", null, 1.0, groupName);
        this.currentTime = new SimpleSettingsProperty("Current Time", null, 0.0, groupName);
        this.playOnActive = new SimpleSettingsProperty('Play on Active', true, true, groupName);
        
        const renderGroup = "Rendering";
        this.blendMode = new SimpleSettingsProperty<BlendMode>('Blend Mode', 'normal', 'normal', renderGroup, ['normal', 'additive', 'subtractive', 'multiply']);
        
        // Link to POI data
        this.autoplay.addParent(poiData.videoAutoplay);
        this.loop.addParent(poiData.videoLoop);
        this.muted.addParent(poiData.videoMuted);
        
        // // Sensible defaults for a video material - values are set, material is not created.
        // this.color.setImmediate(new Color(1,1,1));
        // this.emissive.setImmediate(new Color(1,1,1));
        // this.emissiveIntensity.setImmediate(1.0);
        // this.metalness.setImmediate(0.0);
        // this.roughness.setImmediate(1.0);
        
        this.setupPropertyListeners();
        
        // Automatically load video if URL is provided
        if (this.uri.value) {
            // Delay to ensure material is fully initialized
            setTimeout(() => {
                if (this._threeMaterial) {
                    this.loadTexture(this.uri.value!);
                }
            }, 100);
        }
    }
    
    public override getThreeMaterial(): THREE.Material {
        // Use simple MeshBasicMaterial for reliable video playback
        if (!this._threeMaterial) {
            this._threeMaterial = new THREE.MeshBasicMaterial({
                color: 0xffffff,
                side: THREE.DoubleSide
            }) as any;
        }
        
        const material = this._threeMaterial as THREE.MeshStandardMaterial;

        // One-time setup for video-specific material properties
        if (this._video === null) {
            material.blending = this.getBlendingConstant();
        }

        // 🎯 FIX: Do NOT auto-load texture here - this prevents race conditions
        //Logger.Instance.Debug(`🎯 [TIMING] VideoMaterial created without auto-loading texture`, 'MaterialLoading');
        
        // Ensure video texture is always mapped when material is accessed
        if (this._texture && material.map !== this._texture) {
            material.map = this._texture;
            material.needsUpdate = true;
        }
        
        return material;
    }

    /**
     * 🎯 Explicitly request texture loading - called after MaterialLoadingManager registration
     */
    public requestTextureLoad(): void {
        if (!this._video && !this.isLoading && this.uri.value) {
            //Logger.Instance.Debug(`🚀 [TIMING] Explicit video load request for: ${this.uri.value}`, 'MaterialLoading');
            this.loadTexture(this.uri.value);
        }
    }
    
    public override onUpdate(deltaTime: number): void {
        // 🎯 FIX: Call parent onUpdate to sync roughness/metalness properties
        super.onUpdate(deltaTime);

        // If using shared cache, refresh playing state from cache
        const url = this.uri.value;
        const cached = url ? VideoMaterial._cache.get(url) : undefined;
        if (cached && cached.video) {
            this._video = cached.video;
            this._texture = cached.texture;
            // Mirror playing state from the shared element
            this._isPlaying = !cached.video.paused && !cached.video.ended;
        }

        if (!this._video) return;

        // Ensure video texture is always mapped to the material
        if (this._texture && this._threeMaterial) {
            const mat = this._threeMaterial as THREE.MeshStandardMaterial;
            if (mat.map !== this._texture) {
                mat.map = this._texture;
                mat.needsUpdate = true;
            }
            
            // Debug: Check if map gets lost
            if (!mat.map) {
                mat.map = this._texture;
                mat.needsUpdate = true;
            }
        }



        // Simple video texture update - only update when video is actually playing
        if (this._texture) {
            // For shared textures, updating once per frame from any instance is enough.
            // Here we update whenever the shared video is playing.
            if (!this._video.paused && !this._video.ended) {
                this._texture.needsUpdate = true;
            }
        }
        
        // --- 1. Sync properties only when they've actually changed ---
        const newVolume = this.volume.value;
        if (newVolume !== null && Math.abs(this._video.volume - newVolume) > 0.01) {
            this._video.volume = newVolume;
        }

        const newPlaybackRate = this.playbackRate.value;
        if (newPlaybackRate !== null && Math.abs(this._video.playbackRate - newPlaybackRate) > 0.01) {
            this._video.playbackRate = newPlaybackRate;
        }

        const newMuted = this.muted.value;
        if (newMuted !== null && this._video.muted !== newMuted) {
            this._video.muted = newMuted;
        }

        const newLoop = this.loop.value;
        if (newLoop !== null && this._video.loop !== newLoop) {
            this._video.loop = newLoop;
        }

        // Autoplay is handled once during video loading, not continuously in onUpdate
    }
    
    get video(): HTMLVideoElement | null {
        return this._video;
    }
    
    get videoWidth(): number {
        return this._video?.videoWidth || 0;
    }
    
    get videoHeight(): number {
        return this._video?.videoHeight || 0;
    }
    
    get isPlaying(): boolean {
        return this._isPlaying;
    }
    
    get duration(): number {
        return this._video?.duration || 0;
    }
    
    protected loadTexture(url: string, forceEager: boolean = false): void {
        Logger.Instance.Info(`VideoMaterial.loadTexture called for ${url} isLoading=${this.isLoading} force=${forceEager}`, 'Video');
        if (!url || this.isLoading) {
            return;
        }

        // LAZY INIT: When neither autoplay nor "video already in cache" applies, defer the
        // actual <video> + HLS.js construction until setActive() is called. Initializing 10
        // HLS streams up-front saturates the main thread for 20+ seconds.
        // forceEager=true bypasses this — used by setActive() which already wants the video now.
        const eagerAllowed = forceEager || !!this.autoplay.value || VideoMaterial._cache.has(url);
        if (!eagerAllowed) {
            this._deferredLoadUrl = url;
            Logger.Instance.Info(`VideoMaterial.loadTexture DEFERRED until setActive for ${url}`, 'Video');
            return;
        }

        this.disposeTexture(); // Clean up previous instance references if any

        this.isLoading = true;
        this.error = null;

        // Reuse cached video/texture if available
        const cached = VideoMaterial._cache.get(url);
        if (cached) {
            this._video = cached.video;
            this._texture = cached.texture;
            this._hls = cached.hls ?? null;
            cached.ref++;

            // Attach to material
            if (!this._threeMaterial) {
                this._threeMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, side: THREE.DoubleSide }) as any;
            }
            const mat = this._threeMaterial as THREE.MeshStandardMaterial;
            mat.map = this._texture;
            mat.color.setRGB(1, 1, 1);
            mat.transparent = false;
            mat.opacity = 1.0;
            mat.roughness = 1.0;
            mat.metalness = 0.0;
            mat.needsUpdate = true;

            // Ensure aspect ratio reflects the cached asset
            this.originalTextureAspectRatio = cached.aspect;
            this.aspectRatio.value = cached.aspect || 1.0;

            this.isLoading = false;
            return;
        }

        const video = document.createElement('video');
        
        // --- Aggressive Muting for Autoplay ---
        // For autoplay to work reliably, the video MUST be muted before play() is called.
        // We will temporarily ignore the user's setting and force it to be muted.
        // The onUpdate loop will sync it back to the user's desired state after playback starts.
        video.muted = true; 
        
        video.crossOrigin = 'anonymous';
        video.loop = this.loop.value ?? true;
        video.autoplay = this.autoplay.value ?? false;
        video.playsInline = true;
        
        // Diagnostic listeners for failed video loads (HLS issues, CORS, network)
        video.addEventListener('error', () => {
            const code = video.error?.code;
            const msg = video.error?.message;
            Logger.Instance.Error(`Video error event for ${url} code=${code} msg=${msg} networkState=${video.networkState} readyState=${video.readyState}`, 'Video');
        });
        video.addEventListener('stalled', () => Logger.Instance.Warn(`Video stalled for ${url}`, 'Video'));
        video.addEventListener('suspend', () => Logger.Instance.Info(`Video suspend for ${url}`, 'Video'));
        video.addEventListener('loadstart', () => Logger.Instance.Info(`Video loadstart for ${url}`, 'Video'));

        // iOS Safari (native HLS) often fires loadedmetadata before videoWidth/Height are populated.
        // Hook later events as a fallback so the aspect ratio gets set when dimensions become known.
        const tryUpdateAspect = (eventName: string) => {
            if (video.videoWidth > 0 && video.videoHeight > 0) {
                const ratio = video.videoWidth / video.videoHeight;
                if (Math.abs((this.aspectRatio.value ?? 1) - ratio) > 0.01) {
                    this.originalTextureAspectRatio = ratio;
                    this.aspectRatio.value = ratio;
                    const cached = VideoMaterial._cache.get(url);
                    if (cached) cached.aspect = ratio;
                    Logger.Instance.Info(`Video aspect updated from ${eventName}: ${ratio} (${video.videoWidth}x${video.videoHeight}) for ${url}`, 'Video');
                }
            }
        };
        video.addEventListener('loadeddata', () => { Logger.Instance.Info(`Video loadeddata for ${url}`, 'Video'); tryUpdateAspect('loadeddata'); });
        video.addEventListener('canplay', () => {
            Logger.Instance.Info(`Video canplay for ${url}`, 'Video');
            tryUpdateAspect('canplay');
            // If setActive() was called before the lazy-loaded video was ready, the play()
            // intent was stored in _pendingPlay. Consume it now that the video can actually play.
            if (this._pendingPlay && !this._manuallyPaused) {
                this._pendingPlay = false;
                Logger.Instance.Info(`VideoMaterial: consuming pendingPlay on canplay (${this.name})`, 'Video');
                this.play().catch(err => Logger.Instance.Warn(`pendingPlay failed: ${err}`));
            }
        });
        video.addEventListener('resize', () => tryUpdateAspect('resize'));
        video.addEventListener('playing', () => tryUpdateAspect('playing'));

        video.onloadedmetadata = () => {
            Logger.Instance.Info(`Video onloadedmetadata fired for ${url} (${video.videoWidth}x${video.videoHeight})`, 'Video');

            if (!this._threeMaterial) { // Check if material was disposed during load
                Logger.Instance.Warn(`Video metadata loaded but material was disposed: ${url}`, 'Video');
                video.src = ''; // Stop loading
                return;
            }

            //Logger.Instance.Info(`Video metadata loaded: ${url}, Size: ${video.videoWidth}x${video.videoHeight}, Duration: ${video.duration}s`);
            
            this._video = video;
            this.updateVideoElementProperties();
            
            // Create VideoTexture with proper settings from the start
            this._texture = new THREE.VideoTexture(video);
            this._texture.colorSpace = THREE.SRGBColorSpace;
            this._texture.magFilter = THREE.LinearFilter;
            this._texture.minFilter = THREE.LinearFilter;
            this._texture.wrapS = THREE.ClampToEdgeWrapping;
            this._texture.wrapT = THREE.ClampToEdgeWrapping;
            this._texture.flipY = true; // FIX: TRUE for correct video orientation!
            this._texture.needsUpdate = true;
            
            const mat = this._threeMaterial as THREE.MeshStandardMaterial;
            mat.map = this._texture;
            
            // Essential material setup for video display - force white color for proper video rendering
            mat.color.setRGB(1, 1, 1); // Pure white - essential for video textures!
            //  mat.emissive.setRGB(0, 0, 0); // No emissive glow
            mat.emissiveMap = null;
            mat.transparent = false;
            mat.opacity = 1.0;
            mat.roughness = 1.0; // No reflections on video
            mat.metalness = 0.0; // No metallic properties
            
            this._threeMaterial.needsUpdate = true;
            
            

            // Calculate and set correct aspect ratio
            const videoAspectRatio = (this._video.videoWidth > 0 && this._video.videoHeight > 0)
                ? this._video.videoWidth / this._video.videoHeight
                : 1.0;
                
            this.originalTextureAspectRatio = videoAspectRatio;
            this.aspectRatio.value = videoAspectRatio;
            Logger.Instance.Info(`Video aspectRatio set to ${videoAspectRatio} for ${url}`, 'Video');

            // Store in cache for reuse by other POIs
            VideoMaterial._cache.set(url, { video, texture: this._texture, hls: this._hls, ref: 1, aspect: videoAspectRatio });

            


            this.updateTextureProperties();


            this.isLoading = false;
            
            //Logger.Instance.Info(`Video texture created successfully for: ${url}`);
            
            // Autoplay logic - try once when video loads, not continuously
            
            if (this.autoplay.value) {
                this.play().catch(e => {
                    this.error = `Autoplay was prevented. User interaction may be required.`;
                    Logger.Instance.Error(`Autoplay for ${url} was prevented: ${e}`);
                    console.error(`❌ Autoplay for ${url} was prevented:`, e);
                    ////console.log(`🖱️ Click anywhere or press any key to start the video manually!`);
                });
            }
        };
        
        video.oncanplay = () => {
            // //Logger.Instance.Info(`Video can start playing: ${url}`);
        };
        
        video.oncanplaythrough = () => {
            // //Logger.Instance.Info(`Video can play through: ${url}`);
        };
        
        video.onerror = () => {
            this.isLoading = false;
            // The error event for <video> is not very descriptive on its own.
            // The video.error property contains more info.
            let errorMsg = `Failed to load video from ${url}.`;
            if (video.error) {
                switch (video.error.code) {
                    case video.error.MEDIA_ERR_ABORTED:
                        errorMsg += ' The fetching process was aborted by the user.';
                        break;
                    case video.error.MEDIA_ERR_NETWORK:
                        errorMsg += ' A network error caused the video download to fail.';
                        break;
                    case video.error.MEDIA_ERR_DECODE:
                        errorMsg += ' The video playback was aborted due to a corruption problem or because the video used features your browser did not support.';
                        break;
                    case video.error.MEDIA_ERR_SRC_NOT_SUPPORTED:
                        errorMsg += ' The video could not be loaded, either because the server or network failed or because the format is not supported.';
                        break;
                    default:
                        errorMsg += ' An unknown error occurred.';
                        break;
                }
            }
            this.error = errorMsg;
            Logger.Instance.Error(`VideoMaterial '${this.name}': ${errorMsg}`);
        };
        
        video.onplay = () => { 
            //Logger.Instance.Debug(`Video playing: ${url}`);
            this._isPlaying = true;
            // //Logger.Instance.Info(`✅ Video STARTED playing: ${url} at time ${this._video?.currentTime}s`);
            ////console.log(`▶️ Video STARTED playing: ${url} at time ${this._video?.currentTime}s`);
        };
        video.onpause = () => { 
            //Logger.Instance.Debug(`Video paused: ${url}`);
            this._isPlaying = false;
            this._manuallyPaused = false; // Clear manual pause when video ends naturally
            // //Logger.Instance.Info(`🏁 Video ENDED: ${url}`);
        };
        video.onended = () => { 
            //Logger.Instance.Debug(`Video ended: ${url}`);
            this._isPlaying = false;
            this._manuallyPaused = false; // Clear manual pause when video ends naturally
            // //Logger.Instance.Info(`🏁 Video ENDED: ${url}`);
        };
        
        // Detect HLS streams and attach via native support or hls.js
        const isHls = /\.m3u8(?:$|\?)/i.test(url) || url.toLowerCase().includes('master.m3u8');
        if (isHls) {
            try {
                // Native HLS (Safari, some iOS WKWebView)
                if (video.canPlayType('application/vnd.apple.mpegURL')) {
                    video.src = url;
                    video.load();
                } else {
                    // Dynamic import to avoid SSR/build-time issues
                    (async () => {
                        try {
                            const { default: Hls } = await import('hls.js');
                            if (Hls && (Hls as any).isSupported()) {
                                Logger.Instance.Info(`HLS.js attaching to ${url}`, 'Video');
                                this._hls = new (Hls as any)({
                                    enableWorker: true,
                                    lowLatencyMode: true
                                });
                                this._hls.on((Hls as any).Events.MANIFEST_LOADING, () => Logger.Instance.Info(`HLS MANIFEST_LOADING ${url}`, 'Video'));
                                this._hls.on((Hls as any).Events.MANIFEST_LOADED, () => Logger.Instance.Info(`HLS MANIFEST_LOADED ${url}`, 'Video'));
                                this._hls.on((Hls as any).Events.MANIFEST_PARSED, () => Logger.Instance.Info(`HLS MANIFEST_PARSED ${url}`, 'Video'));
                                this._hls.loadSource(url);
                                this._hls.attachMedia(video);
                                this._hls.on((Hls as any).Events.ERROR, (_evt: any, data: any) => {
                                    Logger.Instance.Warn(`HLS error type=${data?.type} details=${data?.details} fatal=${data?.fatal} for ${url}`, 'Video');
                                    if (data?.fatal) {
                                        this.error = `HLS fatal error: ${data?.type || 'unknown'}`;
                                    }
                                });
                            } else {
                                this.error = 'HLS not supported in this browser';
                                Logger.Instance.Error(this.error);
                            }
                        } catch (e) {
                            this.error = `Failed to initialize hls.js: ${e}`;
                            Logger.Instance.Error(String(e));
                        }
                    })();
                }
            } catch (e) {
                this.error = `HLS setup error: ${e}`;
                Logger.Instance.Error(String(e));
            }
        } else {
            // Regular file-based video (mp4/webm/ogg)
            video.src = url;
            video.load();
        }
    }
    
    protected updateTextureProperties(): void {
        if (this._texture) {

            const threeTexture = this._texture as THREE.VideoTexture;
            
            const wrapMap = {
                'repeat': THREE.RepeatWrapping,
                'clampToEdge': THREE.ClampToEdgeWrapping,
                'mirroredRepeat': THREE.MirroredRepeatWrapping,
            };

            const magFilterMap = {
                'nearest': THREE.NearestFilter,
                'linear': THREE.LinearFilter,
                'nearestMipmapNearest': THREE.NearestFilter,
                'linearMipmapNearest': THREE.LinearFilter,
                'nearestMipmapLinear': THREE.NearestFilter,
                'linearMipmapLinear': THREE.LinearFilter,
            };

            const minFilterMap = {
               'nearest': THREE.NearestFilter,
               'linear': THREE.LinearFilter,
               'nearestMipmapNearest': THREE.NearestMipmapNearestFilter,
               'linearMipmapNearest': THREE.LinearMipmapNearestFilter,
               'nearestMipmapLinear': THREE.NearestMipmapLinearFilter,
               'linearMipmapLinear': THREE.LinearMipmapLinearFilter,
            };

            // DON'T override video texture filters - they need specific settings
            // threeTexture.wrapS = wrapMap[props.wrapS!];
            // threeTexture.wrapT = wrapMap[props.wrapT!];
            // threeTexture.magFilter = magFilterMap[props.magFilter!];
            // threeTexture.minFilter = minFilterMap[props.minFilter!];

            threeTexture.repeat.set(this.repeatX.value ?? 1, this.repeatY.value ?? 1);
            threeTexture.offset.set(this.offsetX.value ?? 0, this.offsetY.value ?? 0);
            threeTexture.rotation = (this.rotation.value ?? 0) * (Math.PI / 180);
            threeTexture.center.set(0.5, 0.5);
            threeTexture.flipY = this.flipY.value ?? true;
            threeTexture.premultiplyAlpha = this.premultiplyAlpha.value ?? false;
            
            threeTexture.needsUpdate = true;
        }
    }
    
    private updateVideoElementProperties(): void {
        if (this._video) {
            this._video.volume = this.volume.value ?? 1.0;
            this._video.playbackRate = this.playbackRate.value ?? 1.0;
            if (this.currentTime.value !== null) {
            this._video.currentTime = this.currentTime.value;
            }
            this._video.muted = this.muted.value ?? false;
            this._video.loop = this.loop.value ?? true;
        }
    }
    
    protected setupPropertyListeners(): void {
        super.setupPropertyListeners(); // Sets up PBR listeners AND uri.on() in TexturedMaterial
        
        // Listeners for video-specific properties
        this.autoplay.on(() => this.updateVideoElementProperties());
        this.loop.on(() => this.updateVideoElementProperties());
        this.muted.on(() => this.updateVideoElementProperties());
        this.playbackRate.on(() => this.updateVideoElementProperties());
        
        // 🎯 Initial video load if URL is already set - will be triggered by requestTextureLoad()
    }
    
    // Video playback controls
    async play(): Promise<void> {
        if (this._video && this._video.paused) {
            try {
                //Logger.Instance.Debug(`VideoMaterial '${this.name}': Attempting to play video.`);
                this._manuallyPaused = false; // Clear manual pause flag when playing
                await this._video.play();
                this._isPlaying = true;
                // //Logger.Instance.Info(`VideoMaterial '${this.name}': Play command succeeded.`);
            } catch (error) {
                this._isPlaying = false;
                const errorMessage = error instanceof Error ? error.message : String(error);
                Logger.Instance.Error(`VideoMaterial '${this.name}': Play command failed: ${errorMessage}`);
                this.error = `Video playback failed. The browser may require user interaction to start the video.`;
            }
        }
    }
    
    pause(): void {
        if (this._video) {
            this._video.pause();
            this._isPlaying = false;
            this._manuallyPaused = true; // Set manual pause flag
            //console.log('🎬 Video manually paused - blocking auto-play');
        }
    }
    
    // Toggle play/pause
    async togglePlayPause(): Promise<void> {
        if (!this._video) return;
        
        if (this._video.paused) {
            await this.play();
        } else {
            this.pause();
        }
    }

    // Override Material base class methods for activation
    public override setActive(): void {
        // Lazy-init: if loadTexture was deferred, run it now so the video is ready to play.
        if (this._deferredLoadUrl) {
            const url = this._deferredLoadUrl;
            this._deferredLoadUrl = null;
            Logger.Instance.Info(`VideoMaterial: triggering deferred load on setActive for ${url}`, 'Video');
            this.loadTexture(url, true); // force eager — we explicitly want the video now
        }

        if (!this.playOnActive.value || this._manuallyPaused) return;

        // If video element doesn't exist yet (lazy load just kicked off + HLS.js async import),
        // defer the play() call. Hook canplay/loadedmetadata to play once ready.
        if (!this._video) {
            this._pendingPlay = true;
            Logger.Instance.Info(`VideoMaterial: play deferred until video ready (${this.name})`, 'Video');
            return;
        }
        this.play().catch(error => {
            Logger.Instance.Warn(`Failed to auto-play video on activation: ${error}`);
        });
    }

    public override setInactive(): void {
        // Clear any deferred play intent — user navigated away before video became ready.
        this._pendingPlay = false;
        // Always pause when becoming inactive, but preserve manual pause state
        if (this._isPlaying) {
            if (this._video) {
                this._video.pause();
                this._isPlaying = false;
                // Note: We don't set _manuallyPaused = true here because this is an automatic pause
                // The _manuallyPaused flag should only be true when user explicitly pauses
                // //Logger.Instance.Info(`🎬 Video auto-paused due to POI becoming inactive: ${this.name}`);
            }
        } else {
            //Logger.Instance.Debug(`🎬 Video was already paused when POI became inactive: ${this.name}`);
        }
    }
    
    // Force play video (for debugging/testing)
    async forcePlay(): Promise<boolean> {
        if (!this._video) {
            Logger.Instance.Warn(`VideoMaterial '${this.name}': No video element loaded`);
            return false;
        }
        
        try {
            //Logger.Instance.Info(`VideoMaterial '${this.name}': Force playing video (${this._video.src})`);
            await this._video.play();
            this._isPlaying = true;
            //Logger.Instance.Info(`VideoMaterial '${this.name}': Force play succeeded`);
            return true;
        } catch (error) {
            this._isPlaying = false;
            const errorMessage = error instanceof Error ? error.message : String(error);
            Logger.Instance.Error(`VideoMaterial '${this.name}': Force play failed: ${errorMessage}`);
            return false;
        }
    }
    
    setCurrentTime(time: number): void {
        this.currentTime.value = time;
        if (this._video) {
            this._video.currentTime = time;
        }
    }
    
    setVolume(volume: number): void {
        this.volume.value = Math.max(0, Math.min(1, volume));
    }
    
    setPlaybackRate(rate: number): void {
        this.playbackRate.value = rate;
    }
    
    // Serialize material
    serialize(): any {
        return {
            ...super.serialize(),
            // VideoMaterial properties
            videoUrl: this.uri.value,
            autoplay: this.autoplay.value,
            loop: this.loop.value,
            muted: this.muted.value,
            volume: this.volume.value,
            playbackRate: this.playbackRate.value,
            currentTime: this.currentTime.value,
            aspectRatio: this.aspectRatio.value,
            videoWidth: this.videoWidth,
            videoHeight: this.videoHeight,
            duration: this.duration,
            isPlaying: this._isPlaying,
        };
    }
    
    protected getBlendingConstant(): THREE.Blending {
        switch (this.blendMode.value) {
            case 'normal': return THREE.NormalBlending;
            case 'additive': return THREE.AdditiveBlending;
            case 'subtractive': return THREE.SubtractiveBlending;
            case 'multiply': return THREE.MultiplyBlending;
            default: return THREE.NormalBlending;
        }
    }
    
    // Dispose resources
    dispose(): void {
        super.dispose();
        this.disposeTexture();
    }

    protected disposeTexture(): void {
        this.pause();

        // Try shared-cache release first
        const url = this.uri.value;
        if (url && VideoMaterial._cache.has(url)) {
            const entry = VideoMaterial._cache.get(url)!;
            entry.ref--;
            if (entry.ref <= 0) {
                // Final owner: dispose underlying resources
                try { entry.hls?.destroy?.(); } catch {}
                entry.video.src = '';
                entry.video.load();
                entry.texture.dispose();
                VideoMaterial._cache.delete(url);
            }
            // Clear instance references only
            this._hls = null;
            this._video = null;
            this._texture = null;
            if (this._threeMaterial) {
                this._threeMaterial.map = null;
                this._threeMaterial.needsUpdate = true;
            }
            this.aspectRatio.value = 1.0;
            this.isLoading = false;
            this._isPlaying = false;
            this._manuallyPaused = false;
            return;
        }

        // Fallback per-instance cleanup (non-cached)
        if (this._hls) { try { this._hls.destroy?.(); } catch {} this._hls = null; }
        if (this._video) { this._video.src = ''; this._video.load(); this._video = null; }
        if (this._texture) { this._texture.dispose(); this._texture = null; this.aspectRatio.value = 1.0; }
        if (this._threeMaterial) { this._threeMaterial.map = null; this._threeMaterial.needsUpdate = true; }
        this.isLoading = false;
        this._isPlaying = false;
        this._manuallyPaused = false; // Reset manual pause flag
    }

    // Debug method to check video state
    debugVideoState(): void {
        ////console.log(`=== VideoMaterial Debug: ${this.name} ===`);
        //console.log('Video element:', this._video);
        //console.log('Video src:', this._video?.src);
        //console.log('Video ready state:', this._video?.readyState);
        //console.log('Video dimensions:', `${this.videoWidth}x${this.videoHeight}`);
        //console.log('Video duration:', this.duration);
        //console.log('Video current time:', this._video?.currentTime);
        //console.log('Video paused:', this._video?.paused);
        //console.log('Video muted:', this._video?.muted);
        //console.log('Is playing:', this._isPlaying);
        //console.log('Texture:', this._texture);
        //console.log('Texture URL:', this.uri.value);
        //console.log('Material:', this._threeMaterial);
        //console.log('Material map:', this._threeMaterial?.map);
        //console.log('Loading state:', this.isLoading);
        //console.log('Error:', this.error);
        //console.log('=====================================');
    }
    

    
    // Method to fix common video texture issues
    fixVideoDisplay(): void {
        if (!this._video || !this._texture || !this._threeMaterial) {
            //console.log('❌ Cannot fix video display - missing components');
            return;
        }
        
        //console.log('🔧 Attempting to fix video display...');
        
        const mat = this._threeMaterial as THREE.MeshStandardMaterial;
        const videoTexture = this._texture as THREE.VideoTexture;
        
        // Force texture update
        videoTexture.needsUpdate = true;
        
        // Reset material properties for video display
        mat.map = videoTexture;
        mat.color = new THREE.Color(1, 1, 1); // Pure white
        mat.emissive = new THREE.Color(0, 0, 0); // No emissive
        mat.emissiveMap = null;
        mat.transparent = false;
        mat.opacity = 1.0;
        
        // Video-specific texture settings
        videoTexture.colorSpace = THREE.SRGBColorSpace;
        videoTexture.magFilter = THREE.LinearFilter;
        videoTexture.minFilter = THREE.LinearFilter;
        videoTexture.wrapS = THREE.ClampToEdgeWrapping;
        videoTexture.wrapT = THREE.ClampToEdgeWrapping;
        videoTexture.flipY = false; // Important for video textures
        
        mat.needsUpdate = true;
        
        //console.log('✅ Video display fix applied');
        //console.log('Video ready state:', this._video.readyState);
        //console.log('Video current time:', this._video.currentTime);
        //console.log('Video playing:', !this._video.paused);
    }
    
    // Test method to create a simple test pattern
    createTestPattern(): void {
        if (!this._threeMaterial) return;
        
        // Create a simple canvas texture for testing
        const canvas = document.createElement('canvas');
        canvas.width = 256;
        canvas.height = 256;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
            // Create a test pattern
            ctx.fillStyle = '#ff0000';
            ctx.fillRect(0, 0, 128, 128);
            ctx.fillStyle = '#00ff00';
            ctx.fillRect(128, 0, 128, 128);
            ctx.fillStyle = '#0000ff';
            ctx.fillRect(0, 128, 128, 128);
            ctx.fillStyle = '#ffff00';
            ctx.fillRect(128, 128, 128, 128);
            
            const testTexture = new THREE.CanvasTexture(canvas);
            const mat = this._threeMaterial as THREE.MeshStandardMaterial;
            mat.map = testTexture;
            mat.needsUpdate = true;
            
            //Logger.Instance.Info(`VideoMaterial '${this.name}': Applied test pattern`);
        }
    }
}